import monthName from 'utils/enums/months';

/**
 * it gets 3 first characters of the string, expected a Month Name
 * @param {string} month Month Name to format
 */
export const getShortMonthName = (month) => {
  const totalCharacterToGet = 3;
  const startPosition = 0;
  if (month) {
    return month.substring(startPosition, totalCharacterToGet);
  }
  return '';
};

/**
 * This converts a string into a Customs formated Date
 * Mon dd, YYYY, TimeStamp AM/PM Format
 * @param {string} date Send date to parese and get the format
 */
export function getCustomFormattedDate(date) {
  if (!Number.isNaN(Date.parse(date))) {
    const parseDate = new Date(date);
    const localesFormat = 'default';
    const intlMonthOption = 'long';
    let shortMonthName = getShortMonthName(
      parseDate.toLocaleString(localesFormat, { month: intlMonthOption }),
    );

    if (shortMonthName) {
      shortMonthName = `${shortMonthName[0].toUpperCase()}${
        shortMonthName.length > 1 ? shortMonthName.substring(1) : ''
      }`;
    }

    return `${shortMonthName} ${parseDate.getDate()}, ${parseDate.getFullYear()}, ${parseDate.toLocaleTimeString(
      localesFormat,
      {
        hour12: false,
      },
    )}`;
  }

  return '';
}

/**
 * Convert date string to 'YYYY-MM-DD' format
 * @param {string} date Date to parse
 * @returns Date in 'YYYY-MM-DD' format
 */
export function getYearMonthDayFormatDate(date) {
  if (!Number.isNaN(Date.parse(date))) {
    const parseDate = new Date(date);
    return `${parseDate.getFullYear()}-${(parseDate.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${parseDate.getDate().toString().padStart(2, '0')}`;
  }
  return '';
}

/**
 * Get the month name by month number
 * @param {Number} month Month number
 * @param {boolean} isShort Flag to return short format
 * @returns Month name
 */
export function getMonthNameByNumber(month, isShort = false) {
  const nameMonth = Object.keys(monthName).find((key) => monthName[key.toString()] === month);
  return isShort ? getShortMonthName(nameMonth) : nameMonth;
}

/**
 * Get the quarter from a date
 * @param {string} date Date to parse
 * @returns Quarter number of the date
 */
export function getQuarterByDate(date) {
  if (!Number.isNaN(Date.parse(date))) {
    const parseDate = new Date(date);
    if (parseDate.getMonth() < 3) {
      return '1';
    }
    if (parseDate.getMonth() < 6) {
      return '2';
    }
    if (parseDate.getMonth() < 9) {
      return '3';
    }
    return '4';
  }
  return '';
}

/**
 *
 * @param {String} date Date to parse
 * @param {*} monthShort Flag to return month in short format
 * @returns Date in 'MMM DD, YYYY, hh:mm:ss a' format
 */
export function getFullDate(date, monthShort = false) {
  if (!Number.isNaN(Date.parse(date))) {
    const parseDate = new Date(date);
    const hours = parseDate.getHours() > 12 ? parseDate.getHours() - 12 : parseDate.getHours();
    const minutes = parseDate.getMinutes().toString().padStart(2, '0');
    const seconds = parseDate.getSeconds().toString().padStart(2, '0');
    return `${getMonthNameByNumber(parseDate.getMonth() + 1, monthShort)} ${parseDate
      .getDate()
      .toString()
      .padStart(2, '0')}, ${parseDate.getFullYear()}, ${hours
      .toString()
      .padStart(2, '0')}:${minutes}:${seconds} ${parseDate.getHours() >= 12 ? 'pm' : 'am'}`;
  }
  return '';
}

/**
 * getFullFormatedDate
 * @param {String} date Date to parse
 * @returns Date in 'YYYY-MM-DD hh:mm:ss.SSS' format
 */
export function getFullFormatedDate(date) {
  if (!Number.isNaN(Date.parse(date))) {
    const parseDate = new Date(date);
    const hours = parseDate.getHours();
    const minutes = parseDate.getMinutes().toString().padStart(2, '0');
    const seconds = parseDate.getSeconds().toString().padStart(2, '0');
    const miliseconds = parseDate.getSeconds().toString().padStart(3, '0');
    return `${parseDate.getFullYear()}-${(parseDate.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${parseDate.getDate().toString().padStart(2, '0')} ${hours
      .toString()
      .padStart(2, '0')}:${minutes}:${seconds}.${miliseconds}`;
  }
  return '';
}

/**
 * compare24HourStringTimes
 * @param {String} fromTime HH:MM AM/PM
 * @param {String} toTime HH:MM AM/PM
 * @returns {boolean} true if fromTime is greater than toTime after accounting AM/PM differences
 */
export function compare12HourStringTimes(fromTime, toTime) {
  if (
    fromTime &&
    toTime &&
    typeof fromTime === 'string' &&
    typeof toTime === 'string' &&
    fromTime.length > 6 &&
    toTime.length > 6
  ) {
    const fromTimeAMPM = fromTime.substring(fromTime.length - 2, fromTime.length);
    const toTimeAMPM = toTime.substring(toTime.length - 2, toTime.length);
    let fromTimeHours24h =
      fromTimeAMPM.toUpperCase()?.includes('PM') &&
      Number(fromTime.substring(0, fromTime.indexOf(':'))) !== 12
        ? Number(fromTime.substring(0, fromTime.indexOf(':'))) + 12
        : Number(fromTime.substring(0, fromTime.indexOf(':')));
    let toTimeHours24h =
      toTimeAMPM.toUpperCase()?.includes('PM') &&
      Number(toTime.substring(0, toTime.indexOf(':'))) !== 12
        ? Number(toTime.substring(0, toTime.indexOf(':'))) + 12
        : Number(toTime.substring(0, toTime.indexOf(':')));
    fromTimeHours24h =
      fromTimeHours24h === 12 && fromTimeAMPM?.includes('AM') ? 0 : fromTimeHours24h;
    toTimeHours24h = toTimeHours24h === 12 && toTimeAMPM?.includes('AM') ? 0 : toTimeHours24h;
    if (fromTimeHours24h > toTimeHours24h) {
      return true;
    }
    return (
      fromTimeHours24h >= toTimeHours24h &&
      Number(fromTime.substring(fromTime.indexOf(':') + 1, fromTime.indexOf(' '))) >
        Number(toTime.substring(toTime.indexOf(':') + 1, toTime.indexOf(' ')))
    );
  }
  return false;
}

/**
 * convert12hTimeTo24hTime
 * @param {String} time HH:MM AM/PM
 * @returns the time string in HH:MM 24h format, without AM/PM
 */
export function convert12hTimeTo24hTime(time12h) {
  if (time12h && time12h.length > 6 && typeof time12h === 'string') {
    const [time, modifier] = time12h.split(' ');
    const timeArray = time.split(':');
    let hours = timeArray[0];
    const minutes = timeArray[1];
    if (hours === '12') {
      hours = '00';
    }
    if (modifier.toUpperCase() === 'PM') {
      hours = parseInt(hours, 10) + 12;
    }

    return `${hours}:${minutes}`;
  }
  return time12h;
}
